import {
	editSensorDataPoints,
	editSensorDataPointsRejected,
	editSensorDataPointsResolved,
	getSensorHistory,
	getSensorHistoryRejected,
	getSensorHistoryResolved,
	peekSensorComputeQuery,
	peekSensorConnection,
	peekSensorConnectionRejected,
	peekSensorConnectionResolved,
	peekSensorFieldChange,
	peekSensorReset,
	pollSensorTroubleshooting,
	pollSensorTroubleshootingRejected,
	pollSensorTroubleshootingResolved,
	pollSensorStateUpdate,
	troubleshootSensor,
	troubleshootSensorRejected,
	troubleshootSensorResolved,
	pollSensorTroubleshootingStop,
	peekSensorAsyncResolved,
	pollPeekAsyncResultsResolved,
	peekSensorAsync,
	peekSensorAsyncRejected,
	pollPeekAsyncResultsRejected,
	pollPeekAsyncResultsStop,
} from '@Actions';
import { createReducer } from '@reduxjs/toolkit';
import { SensorHistoryStoreState } from '@Types';
import filterDuplicatesDataOverride from '@Utils/filterDuplicatesDataOverride';

export const initialState: SensorHistoryStoreState = {
	sensorById: undefined,
	history: [],
	initialized: false,
	status: 'idle',
	sensorDataOverride: [],
	peek: {
		status: 'idle',
		statusTblt: 'idle',
		queryComputing: 'idle',
		response: {
			data: [],
			fields: {},
		},
		responseAsync: {
			uid: '',
			operationResult: '',
		},
		query: '',
		troubleshooting: {},
	},
};

const reducer = createReducer(initialState, builder =>
	builder
		.addCase(getSensorHistory, state => {
			state.sensorById = undefined;
			state.history = [];
			state.initialized = false;
			state.status = 'idle';
		})
		.addCase(getSensorHistoryResolved, (state, action) => {
			state.sensorById = action.payload.sensorById;
			state.history = action.payload.history;
			state.initialized = true;
			state.reload = undefined;
			state.status = 'idle';
		})
		.addCase(getSensorHistoryRejected, state => {
			state.sensorById = undefined;
			state.history = [];
			state.initialized = true;
			state.reload = undefined;
			state.status = 'idle';
		})
		.addCase(editSensorDataPoints, state => {
			state.status = 'pending';
		})
		.addCase(editSensorDataPointsResolved, (state, action) => {
			const newSensorDataPoints = action.payload;
			const oldDataOverride = state.sensorDataOverride;
			const newDataOverride = filterDuplicatesDataOverride(
				oldDataOverride,
				newSensorDataPoints.map(el => [
					new Date(el.timeStamp).getTime(),
					el.newValue,
				]),
			);
			state.reload = true;
			state.status = 'success';
			state.sensorDataOverride = newDataOverride;
		})
		.addCase(editSensorDataPointsRejected, state => {
			state.status = 'error';
		})
		.addCase(peekSensorConnection, state => {
			state.peek.status = 'pending';
			state.peek.troubleshooting = {};
		})
		.addCase(peekSensorAsync, state => {
			state.peek.status = 'pending';
			state.peek.troubleshooting = {};
		})
		.addCase(peekSensorConnectionResolved, (state, action) => {
			state.peek.status = 'success';
			state.peek.response.data = action.payload as never;
			state.peek.response.fields = {};
		})
		.addCase(peekSensorAsyncResolved, (state, action) => {
			state.peek.response.data = [];
			state.peek.response.fields = {};
			state.peek.responseAsync = action.payload;
		})
		.addCase(pollPeekAsyncResultsResolved, (state, action) => {
			state.peek.status = 'success';
			state.peek.response.data = action.payload as never;
			state.peek.response.fields = {};
		})
		.addCase(peekSensorConnectionRejected, state => {
			state.peek.status = 'error';
			state.peek.response.data = [];
			state.peek.response.fields = {};
		})
		.addCase(peekSensorAsyncRejected, state => {
			state.peek.status = 'error';
			state.peek.response.data = [];
			state.peek.response.fields = {};
		})
		.addCase(pollPeekAsyncResultsRejected, state => {
			state.peek.status = 'pending';
			state.peek.response.data = [];
			state.peek.response.fields = {};
		})
		.addCase(pollPeekAsyncResultsStop, state => {
			state.peek.status = 'idle';
			state.peek.response.data = [];
			state.peek.response.fields = {};
		})
		.addCase(peekSensorReset, state => {
			state.peek.status = 'idle';
			state.peek.response.data = [];
			state.peek.response.fields = {};
			state.peek.troubleshooting = {};
		})
		.addCase(peekSensorFieldChange, state => {
			state.peek.queryComputing = 'pending';
		})
		.addCase(peekSensorComputeQuery, (state, action) => {
			state.peek.queryComputing = 'success';
			state.peek.query = action.payload;
		})
		.addCase(troubleshootSensor, state => {
			state.peek.troubleshooting = {};
			state.peek.status = 'pending';
			state.peek.statusTblt = 'pending';
		})
		.addCase(troubleshootSensorResolved, state => {
			state.peek.status = 'pending'; // ? poll will update this
			state.peek.statusTblt = 'pending'; // ? poll will update this
		})
		.addCase(troubleshootSensorRejected, state => {
			state.peek.status = 'error';
			state.peek.statusTblt = 'error';
		})
		.addCase(pollSensorTroubleshooting, state => {
			state.peek.status = 'pending';
			state.peek.statusTblt = 'pending';
		})
		.addCase(pollSensorTroubleshootingResolved, (state, action) => {
			const { result } = action.payload;
			state.peek.status = 'success';
			state.peek.statusTblt = 'success';
			state.peek.troubleshooting = result;
		})
		.addCase(pollSensorTroubleshootingRejected, state => {
			state.peek.status = 'error';
			state.peek.statusTblt = 'error';
			state.peek.troubleshooting = {};
		})
		.addCase(pollSensorTroubleshootingStop, state => {
			state.peek.status = 'idle';
			state.peek.statusTblt = 'idle';
			state.peek.troubleshooting.state = 'CLEARED';
		})
		.addCase(pollSensorStateUpdate, (state, action) => {
			const { result } = action.payload;
			state.peek.status = 'pending';
			state.peek.statusTblt = 'pending';
			state.peek.troubleshooting = result;
		}),
);

export default reducer;
